import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';

const PWAContext = createContext();

export const PWAProvider = ({ children }) => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [installDismissed, setInstallDismissed] = useState(() => {
    // Check if user dismissed the prompt (with 7-day expiry)
    const dismissed = localStorage.getItem('pwa_install_dismissed');
    if (!dismissed) return false;

    const dismissedAt = parseInt(dismissed, 10);
    const sevenDays = 7 * 24 * 60 * 60 * 1000;

    // If more than 7 days have passed, show again
    if (Date.now() - dismissedAt > sevenDays) {
      localStorage.removeItem('pwa_install_dismissed');
      return false;
    }
    return true;
  });

  // Check if already installed (running in standalone mode)
  useEffect(() => {
    const checkInstalled = () => {
      const isStandalone =
        window.matchMedia('(display-mode: standalone)').matches ||
        window.navigator.standalone || // iOS Safari
        document.referrer.includes('android-app://');

      setIsInstalled(isStandalone);
    };

    checkInstalled();

    // Listen for display mode changes
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    const handleChange = () => checkInstalled();

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
    } else {
      // Fallback for older browsers
      mediaQuery.addListener(handleChange);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleChange);
      } else {
        mediaQuery.removeListener(handleChange);
      }
    };
  }, []);

  // Capture the install prompt event
  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Store the event for later use
      setDeferredPrompt(e);
      setIsInstallable(true);
      console.log('PWA: Install prompt captured');
    };

    const handleAppInstalled = () => {
      setDeferredPrompt(null);
      setIsInstallable(false);
      setIsInstalled(true);
      localStorage.removeItem('pwa_install_dismissed');
      console.log('PWA: App installed successfully');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  // Trigger the install prompt
  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) {
      console.log('PWA: No install prompt available');
      return false;
    }

    try {
      // Show the install prompt
      deferredPrompt.prompt();

      // Wait for the user's response
      const { outcome } = await deferredPrompt.userChoice;

      console.log(`PWA: User ${outcome === 'accepted' ? 'accepted' : 'dismissed'} install prompt`);

      if (outcome === 'accepted') {
        setDeferredPrompt(null);
        setIsInstallable(false);
        return true;
      }

      return false;
    } catch (error) {
      console.error('PWA: Error showing install prompt:', error);
      return false;
    }
  }, [deferredPrompt]);

  // Dismiss the install banner for 7 days
  const dismissInstallPrompt = useCallback(() => {
    setInstallDismissed(true);
    localStorage.setItem('pwa_install_dismissed', Date.now().toString());
    console.log('PWA: Install prompt dismissed for 7 days');
  }, []);

  // Reset dismissal (for settings page if needed)
  const resetDismissal = useCallback(() => {
    setInstallDismissed(false);
    localStorage.removeItem('pwa_install_dismissed');
  }, []);

  const value = {
    isInstallable,
    isInstalled,
    installDismissed,
    promptInstall,
    dismissInstallPrompt,
    resetDismissal,
    // Convenience property: should we show the install prompt?
    showInstallPrompt: isInstallable && !isInstalled && !installDismissed
  };

  return (
    <PWAContext.Provider value={value}>
      {children}
    </PWAContext.Provider>
  );
};

export const usePWA = () => {
  const context = useContext(PWAContext);
  if (!context) {
    throw new Error('usePWA must be used within a PWAProvider');
  }
  return context;
};

export default PWAContext;
